// src/pages/Public/EventPost/PublicEventPostList.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent, CardActions,
  Button, Chip, CircularProgress, Alert, Pagination, Container,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Search as SearchIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const PublicEventPostList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');

  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/public/event-post-categories/${chatbotId}`);
        setCategories(response.data.data || []);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };

    if (chatbotId) loadCategories();
  }, [chatbotId, apiBaseUrl]);

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        let url = `${apiBaseUrl}/api/public/event-posts/${chatbotId}`;
        if (selectedCategory) {
          url = `${apiBaseUrl}/api/public/event-posts/${chatbotId}/category/${selectedCategory}`;
        }

        const response = await axios.get(url, {
          params: { page, per_page: 9 }
        });

        setPosts(response.data.data || []);
        setTotalPages(response.data.pagination?.pages || 1);
      } catch (err) {
        setError('Failed to load posts. Please try again later.');
        console.error('Error loading posts:', err);
      } finally {
        setLoading(false);
      }
    };

    if (chatbotId) loadPosts();
  }, [chatbotId, page, selectedCategory, apiBaseUrl]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Event Posts
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Stay updated with our latest events, news, and community activities
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Filter by Category"
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.slug}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : posts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No posts found
            </Typography>
          </Box>
        ) : (
          <>
            {/* Posts Grid */}
            <Grid container spacing={3}>
              {posts.map(post => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    {post.featured_image_url ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={post.featured_image_url}
                        alt={post.featured_image_alt || post.title}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="h4" color="white" sx={{ opacity: 0.5 }}>
                          Event
                        </Typography>
                      </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Categories */}
                      {post.categories && post.categories.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          {post.categories.slice(0, 2).map(cat => (
                            <Chip
                              key={cat.id}
                              label={cat.name}
                              size="small"
                              sx={{
                                mr: 0.5,
                                mb: 0.5,
                                bgcolor: cat.color,
                                color: '#fff',
                                fontSize: '0.7rem'
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {post.title}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                        <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="caption">
                          {formatDate(post.published_at)}
                        </Typography>
                        {post.author_name && (
                          <Typography variant="caption" sx={{ ml: 2 }}>
                            by {post.author_name}
                          </Typography>
                        )}
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        {truncateText(post.excerpt || '')}
                      </Typography>
                    </CardContent>

                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => navigate(`/posts/${chatbotId}/${post.slug}`)}
                      >
                        Read More
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default PublicEventPostList;
